import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Divider,
  Alert,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  InputAdornment,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Snackbar,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import LockIcon from '@mui/icons-material/Lock';
import ShieldIcon from '@mui/icons-material/Shield';
import GavelIcon from '@mui/icons-material/Gavel';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BusinessIcon from '@mui/icons-material/Business';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DrawIcon from '@mui/icons-material/Draw';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import {
  isSuperAdmin,
  isAgreementCompleted,
  saveAgreementCompletion,
  getAgreementDetails,
  resetAgreementCompletion,
  getMergedAdminComplianceList,
  updateAdminAgreementStatusBySuperAdmin,
} from '../utils/agreementUtils';
import { getDisplayRoleName } from '../utils/roleUtils';

const AdminAgreement = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const superAdmin = isSuperAdmin(user);

  // Super Admin view tabs: 0 = Compliance Tracking List, 1 = Agreement Document Terms
  const [activeTab, setActiveTab] = useState(0);

  // Non-super admin completion state
  const [isCompleted, setIsCompleted] = useState(false);
  const [agreementRecord, setAgreementRecord] = useState(null);
  const [showReaffirm, setShowReaffirm] = useState(false);

  // Form states for non-super admin
  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [organization, setOrganization] = useState('');
  const [agreeTerms, setAgreeTerms] = useState(false);
  const [agreeConfidentiality, setAgreeConfidentiality] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Dual Signing Mode: 0 = Draw Digital Signature, 1 = Upload Signed White Paper Photo, 2 = Type Legal Name
  const [signMode, setSignMode] = useState(0);

  // Digital Signature Canvas
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);

  // White Paper Signed Photo Upload
  const fileInputRef = useRef(null);
  const [paperPhotoData, setPaperPhotoData] = useState({ url: null, name: '', size: '', file: null });
  const [isDragging, setIsDragging] = useState(false);



  // Super Admin compliance tracking states
  const [adminList, setAdminList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL'); // ALL, ACCEPTED, PENDING
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [orgFilter, setOrgFilter] = useState('ALL');
  const [loadingAdmins, setLoadingAdmins] = useState(false);

  // Dialogs
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [signaturePreviewModal, setSignaturePreviewModal] = useState({ open: false, admin: null });
  const [adminUploadModal, setAdminUploadModal] = useState({ open: false, admin: null, photoUrl: null, fileName: '', notes: '' });
  const [reminderDialog, setReminderDialog] = useState({ open: false, admin: null });
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const adminModalFileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL || 'https://gpn.glocalprayer.net/admin-services/api/admin/';

  // Initialize Canvas with crisp white background & signature guideline
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Draw subtle signature baseline
    ctx.beginPath();
    ctx.strokeStyle = '#CBD5E1';
    ctx.lineWidth = 1;
    ctx.moveTo(30, canvas.height - 35);
    ctx.lineTo(canvas.width - 30, canvas.height - 35);
    ctx.stroke();

    ctx.font = '12px sans-serif';
    ctx.fillStyle = '#94A3B8';
    ctx.fillText('✕ Sign above line', 32, canvas.height - 18);
  }, []);

  useEffect(() => {
    if (signMode === 0 && (!isCompleted || showReaffirm)) {
      setTimeout(initCanvas, 50);
    }
  }, [signMode, isCompleted, showReaffirm, initCanvas]);

  // Canvas drawing coordinate calculator
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
    isDrawing.current = true;
  };

  const draw = (e) => {
    if (!isDrawing.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(e);
    ctx.lineTo(coords.x, coords.y);
    ctx.strokeStyle = '#0F2942'; // Dark navy ink
    ctx.lineWidth = 2.8;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    if (!hasDrawnSignature) setHasDrawnSignature(true);
  };

  const stopDrawing = () => {
    isDrawing.current = false;
  };

  const clearSignatureCanvas = () => {
    initCanvas();
    setHasDrawnSignature(false);
  };

  // White Paper Signed Photo Handler
  const handlePaperPhotoSelect = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setFormError('Please select a valid image file (JPG, PNG, WEBP).');
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      setFormError('Photo size exceeds 12MB. Please upload a smaller picture.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setPaperPhotoData({
        url: event.target.result,
        name: file.name,
        size: (file.size / 1024).toFixed(1) + ' KB',
        file: file,
      });
      setFormError('');
    };
    reader.readAsDataURL(file);
  };

  // Fetch EXACT real users from backend database (no dummy accounts)
  const fetchAdminsData = async () => {
    setLoadingAdmins(true);
    try {
      const token = localStorage.getItem('token');
      if (token) {
        const res = await fetch(`${API_URL}users-list`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ page: 1, limit: 200, role: 'All Roles', status: 'All Status' }),
        });
        const data = await res.json();
        if (data && Array.isArray(data.users)) {
          setAdminList(getMergedAdminComplianceList(data.users));
          setLoadingAdmins(false);
          return;
        }
      }
    } catch (e) {
      console.log('Error fetching backend users for compliance tracker:', e);
    }
    setAdminList(getMergedAdminComplianceList([]));
    setLoadingAdmins(false);
  };

  useEffect(() => {
    if (superAdmin) {
      fetchAdminsData();
    } else {
      const status = isAgreementCompleted(user);
      setIsCompleted(status);
      if (status) {
        const details = getAgreementDetails(user);
        setAgreementRecord(details);
      }
      setAdminName(user?.name || user?.username || '');
      setAdminEmail(user?.email || '');
      setOrganization(user?.organization || 'Christian Organization');
    }

    const handleUpdate = () => {
      if (superAdmin) {
        fetchAdminsData();
      } else {
        const status = isAgreementCompleted(user);
        setIsCompleted(status);
        if (status) {
          setAgreementRecord(getAgreementDetails(user));
        }
      }
    };

    window.addEventListener('gpn_admin_agreement_updated', handleUpdate);
    return () => {
      window.removeEventListener('gpn_admin_agreement_updated', handleUpdate);
    };
  }, [user, superAdmin]);

  // Non-super admin submit handler
  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');

    if (!adminName.trim()) {
      setFormError('Please enter your full administrator name.');
      return;
    }
    if (!adminEmail.trim()) {
      setFormError('Please enter your administrative email address.');
      return;
    }
    if (!agreeTerms) {
      setFormError('You must acknowledge and agree to the GPN Administrator Agreement.');
      return;
    }
    if (!agreeConfidentiality) {
      setFormError('You must pledge confidentiality and protection of prayer requests.');
      return;
    }

    let signatureImage = null;
    let signatureType = 'digital_draw';
    let signatureValue = adminName;
    let paperFileName = null;

    if (signMode === 0) {
      // Draw Digital Signature
      if (!hasDrawnSignature || !canvasRef.current) {
        setFormError('Please draw your digital signature on the signature pad above.');
        return;
      }
      signatureImage = canvasRef.current.toDataURL('image/png');
      signatureType = 'digital_draw';
      signatureValue = adminName;
    } else if (signMode === 1) {
      // Upload White Paper Signed Photo
      if (!paperPhotoData.url) {
        setFormError('Please upload a clear photo of your signature written on white paper.');
        return;
      }
      signatureImage = paperPhotoData.url;
      signatureType = 'paper_upload';
      paperFileName = paperPhotoData.name;
      signatureValue = adminName;
    }

    const saved = saveAgreementCompletion(user, {
      adminName,
      adminEmail,
      organization: organization || 'Glocal Prayer Network',
      signature: signatureValue,
      signatureType,
      signatureImage,
      paperFileName,
    });

    if (saved) {
      setIsCompleted(true);
      setAgreementRecord(saved);
      setShowReaffirm(false);
      setSuccessMessage('Thank you! Your GPN Administrator Agreement has been securely recorded and verified.');
    } else {
      setFormError('An error occurred while saving your agreement. Please try again.');
    }
  };

  // Super Admin: toggle acceptance status
  const handleToggleAdminStatus = (adminItem, makeAccepted) => {
    const res = updateAdminAgreementStatusBySuperAdmin(adminItem, makeAccepted);
    if (res) {
      setToast({
        open: true,
        message: makeAccepted
          ? `Agreement marked as Accepted for ${adminItem.name}`
          : `Agreement status marked as Pending for ${adminItem.name}`,
        severity: 'success',
      });
      fetchAdminsData();
    }
  };

  // Super Admin: open upload signed paper modal for a user
  const handleOpenAdminUploadModal = (admin) => {
    setAdminUploadModal({
      open: true,
      admin,
      photoUrl: admin.signatureImage || null,
      fileName: admin.paperFileName || '',
      notes: '',
    });
  };

  const handleAdminModalPhotoSelect = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      setAdminUploadModal((prev) => ({
        ...prev,
        photoUrl: event.target.result,
        fileName: file.name,
      }));
    };
    reader.readAsDataURL(file);
  };

  const handleSaveAdminUploadedPaper = () => {
    if (!adminUploadModal.admin) return;
    if (!adminUploadModal.photoUrl) {
      setToast({ open: true, message: 'Please upload a photo of the signed white paper.', severity: 'error' });
      return;
    }

    updateAdminAgreementStatusBySuperAdmin(adminUploadModal.admin, true, {
      signatureType: 'paper_upload',
      signatureImage: adminUploadModal.photoUrl,
      paperFileName: adminUploadModal.fileName || 'signed_white_paper.jpg',
      signature: adminUploadModal.admin.name,
      acceptedAt: new Date().toISOString(),
    });

    setToast({
      open: true,
      message: `Signed paper verified and agreement marked as Accepted for ${adminUploadModal.admin.name}!`,
      severity: 'success',
    });
    setAdminUploadModal({ open: false, admin: null, photoUrl: null, fileName: '', notes: '' });
    fetchAdminsData();
  };

  // Super Admin: send reminder
  const handleSendReminder = (admin) => {
    setReminderDialog({ open: true, admin });
  };

  const handleConfirmSendReminder = () => {
    setToast({
      open: true,
      message: `Agreement reminder sent successfully to ${reminderDialog.admin?.name} (${reminderDialog.admin?.email})`,
      severity: 'success',
    });
    setReminderDialog({ open: false, admin: null });
  };

  const handleCopyReminderLink = () => {
    const link = `${window.location.origin}/admin/admin-agreement`;
    navigator.clipboard.writeText(
      `Hello ${reminderDialog.admin?.name},\n\nPlease review and complete the GPN Administrator Agreement (draw digital sign or upload photo of signed white paper):\n${link}\n\nThank you,\nGPN Leadership Team`
    );
    setToast({ open: true, message: 'Reminder message and link copied to clipboard!', severity: 'info' });
  };

  // Distinct organizations present in the real database user list
  const uniqueOrganizations = useMemo(() => {
    const orgs = new Set();
    adminList.forEach((admin) => {
      if (admin.organization && typeof admin.organization === 'string' && admin.organization.trim()) {
        orgs.add(admin.organization.trim());
      }
    });
    return Array.from(orgs).sort();
  }, [adminList]);

  // Filtered administrative list for Super Admin
  const filteredAdmins = adminList.filter((admin) => {
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch =
      !q ||
      admin.name?.toLowerCase().includes(q) ||
      admin.email?.toLowerCase().includes(q) ||
      admin.organization?.toLowerCase().includes(q) ||
      admin.verificationCode?.toLowerCase().includes(q);

    const matchesStatus =
      statusFilter === 'ALL' ||
      (statusFilter === 'ACCEPTED' && admin.accepted) ||
      (statusFilter === 'PENDING' && !admin.accepted);

    const matchesRole =
      roleFilter === 'ALL' || String(admin.role) === String(roleFilter);

    const matchesOrg =
      orgFilter === 'ALL' ||
      admin.organization?.toLowerCase().trim() === orgFilter.toLowerCase().trim();

    return matchesSearch && matchesStatus && matchesRole && matchesOrg;
  });

  const totalAdminsCount = adminList.length;
  const acceptedAdminsCount = adminList.filter((a) => a.accepted).length;
  const pendingAdminsCount = totalAdminsCount - acceptedAdminsCount;
  const complianceRate = totalAdminsCount > 0 ? Math.round((acceptedAdminsCount / totalAdminsCount) * 100) : 0;

  // -------------------------------------------------------------
  // RENDER: SUPER ADMIN COMPLIANCE TRACKING & OVERSIGHT VIEW
  // -------------------------------------------------------------
  if (superAdmin) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1440, mx: 'auto' }}>
        {/* Header Banner */}
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/dashboard')}
            sx={{
              color: '#4CA1AF',
              textTransform: 'none',
              mb: 1.5,
              '&:hover': { background: 'rgba(76, 161, 175, 0.1)' },
            }}
          >
            Back to Dashboard
          </Button>

          <Box
            sx={{
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: 'space-between',
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
            }}
          >
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 800,
                    background: 'linear-gradient(90deg, #FFFFFF 0%, #4CA1AF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    fontSize: { xs: '1.6rem', sm: '2rem' },
                  }}
                >
                  Admin Agreement Compliance Tracker
                </Typography>
                <Chip
                  label="Super Admin Oversight"
                  size="small"
                  sx={{
                    background: 'linear-gradient(135deg, #0284C7 0%, #0369A1 100%)',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mt: 0.5 }}>
                Track real users from your database, verify digital signatures or uploaded white paper photos, and manage agreement compliance.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={fetchAdminsData}
              disabled={loadingAdmins}
              sx={{
                color: '#4CA1AF',
                borderColor: 'rgba(76, 161, 175, 0.4)',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { borderColor: '#4CA1AF', background: 'rgba(76, 161, 175, 0.15)' },
              }}
            >
              Refresh Real Data
            </Button>
          </Box>
        </Box>

        {/* Super Admin Notice Card */}
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2.5,
            background: 'linear-gradient(135deg, rgba(2, 132, 199, 0.15) 0%, rgba(15, 23, 42, 0.6) 100%)',
            border: '1px solid rgba(2, 132, 199, 0.35)',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <ShieldIcon sx={{ color: '#38BDF8', fontSize: 28, flexShrink: 0 }} />
          <Typography variant="body2" sx={{ color: '#E0F2FE', fontSize: '0.88rem', lineHeight: 1.5 }}>
            <strong>Super Admin Exemption Notice:</strong> As a Super Administrator, you hold global administrative privileges and <strong>do not need to submit the agreement</strong>. You have oversight authority to verify adherence, inspect digital signatures or signed white paper photos, and issue reminders.
          </Typography>
        </Paper>

        {/* Metric Cards */}
        <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.8) 100%)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontWeight: 600, textTransform: 'uppercase' }}>
                  Total Real Users (Database)
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#FFFFFF', mt: 0.5 }}>
                  {totalAdminsCount}
                </Typography>
                <Typography variant="caption" sx={{ color: '#94A3B8' }}>
                  All members & administrators
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(15, 23, 42, 0.8) 100%)',
                border: '1px solid rgba(16, 185, 129, 0.35)',
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="caption" sx={{ color: '#34D399', fontWeight: 600, textTransform: 'uppercase' }}>
                  Agreements Accepted
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
                  <Typography variant="h4" sx={{ fontWeight: 800, color: '#34D399' }}>
                    {acceptedAdminsCount}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                    ({complianceRate}%)
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  Active & verified
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(15, 23, 42, 0.8) 100%)',
                border: '1px solid rgba(245, 158, 11, 0.35)',
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="caption" sx={{ color: '#FBBF24', fontWeight: 600, textTransform: 'uppercase' }}>
                  Still Pending / Awaiting
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#FBBF24', mt: 0.5 }}>
                  {pendingAdminsCount}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  Action required from users
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card
              sx={{
                background: 'linear-gradient(135deg, rgba(76, 161, 175, 0.15) 0%, rgba(15, 23, 42, 0.8) 100%)',
                border: '1px solid rgba(76, 161, 175, 0.35)',
                borderRadius: 3,
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                <Typography variant="caption" sx={{ color: '#4CA1AF', fontWeight: 600, textTransform: 'uppercase' }}>
                  Overall Compliance Rate
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#4CA1AF', mt: 0.5 }}>
                  {complianceRate}%
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  Target: 100% adherence
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* View Switcher Tabs */}
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            background: 'rgba(30, 41, 59, 0.7)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, val) => setActiveTab(val)}
            textColor="inherit"
            indicatorColor="primary"
            sx={{
              px: 2,
              '& .MuiTabs-indicator': { background: '#4CA1AF', height: 3 },
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.92rem',
                minHeight: 52,
                '&.Mui-selected': { color: '#4CA1AF' },
              },
            }}
          >
            <Tab
              icon={<AssignmentTurnedInIcon sx={{ fontSize: 18, mr: 1 }} />}
              iconPosition="start"
              label={`Real Database Users (${totalAdminsCount})`}
            />
            <Tab
              icon={<GavelIcon sx={{ fontSize: 18, mr: 1 }} />}
              iconPosition="start"
              label="Agreement Policy & Covenant Document"
            />
          </Tabs>
        </Paper>

        {/* TAB 0: COMPLIANCE TRACKING LIST */}
        {activeTab === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
              border: '1px solid rgba(76, 161, 175, 0.25)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            }}
          >
            {/* Search & Filter Bar */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 3,
              }}
            >
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', flex: { xs: '1 1 100%', md: '1 1 auto' } }}>
                {/* Search */}
                <TextField
                  size="small"
                  placeholder="Search user by name, email, or church..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  sx={{
                    width: { xs: '100%', sm: 280, md: 320 },
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 2,
                      color: '#FFFFFF',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                      '&:hover fieldset': { borderColor: '#4CA1AF' },
                      '&.Mui-focused fieldset': { borderColor: '#4CA1AF' },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Organization Filter Dropdown */}
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 220, md: 240 } }}>
                  <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.85rem' }}>Organization</InputLabel>
                  <Select
                    value={orgFilter}
                    label="Organization"
                    onChange={(e) => setOrgFilter(e.target.value)}
                    sx={{
                      color: '#FFFFFF',
                      background: 'rgba(255, 255, 255, 0.05)',
                      borderRadius: 2,
                      fontSize: '0.85rem',
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.15)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#4CA1AF' },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#4CA1AF' },
                      '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          background: '#1e293b',
                          color: '#FFFFFF',
                          border: '1px solid rgba(76, 161, 175, 0.3)',
                          maxHeight: 320,
                        }
                      }
                    }}
                  >
                    <MenuItem value="ALL">All Organizations ({uniqueOrganizations.length})</MenuItem>
                    {uniqueOrganizations.map((org) => (
                      <MenuItem key={org} value={org}>
                        {org}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {orgFilter !== 'ALL' && (
                  <Chip
                    label={`Org: ${orgFilter}`}
                    onDelete={() => setOrgFilter('ALL')}
                    size="small"
                    sx={{
                      background: 'rgba(76, 161, 175, 0.25)',
                      color: '#4CA1AF',
                      border: '1px solid #4CA1AF',
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>

              {/* Status Chips Filter */}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', mr: 0.5 }}>
                  Status:
                </Typography>
                <Chip
                  label={`All (${totalAdminsCount})`}
                  clickable
                  onClick={() => setStatusFilter('ALL')}
                  sx={{
                    background: statusFilter === 'ALL' ? '#4CA1AF' : 'rgba(255, 255, 255, 0.08)',
                    color: '#FFFFFF',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: '15px !important', color: '#34D399 !important' }} />}
                  label={`Accepted (${acceptedAdminsCount})`}
                  clickable
                  onClick={() => setStatusFilter('ACCEPTED')}
                  sx={{
                    background: statusFilter === 'ACCEPTED' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(255, 255, 255, 0.08)',
                    color: '#34D399',
                    border: statusFilter === 'ACCEPTED' ? '1px solid #10B981' : 'none',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  icon={<HourglassEmptyIcon sx={{ fontSize: '15px !important', color: '#FBBF24 !important' }} />}
                  label={`Still Pending (${pendingAdminsCount})`}
                  clickable
                  onClick={() => setStatusFilter('PENDING')}
                  sx={{
                    background: statusFilter === 'PENDING' ? 'rgba(245, 158, 11, 0.3)' : 'rgba(255, 255, 255, 0.08)',
                    color: '#FBBF24',
                    border: statusFilter === 'PENDING' ? '1px solid #F59E0B' : 'none',
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>

            {/* Admins Table */}
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                background: 'transparent',
                borderRadius: 2,
                border: '1px solid rgba(255, 255, 255, 0.08)',
              }}
            >
              <Table>
                <TableHead sx={{ background: 'rgba(255, 255, 255, 0.04)' }}>
                  <TableRow>
                    <TableCell sx={{ color: '#4CA1AF', fontWeight: 700, fontSize: '0.82rem' }}>USER (DATABASE)</TableCell>
                    <TableCell sx={{ color: '#4CA1AF', fontWeight: 700, fontSize: '0.82rem' }}>ROLE</TableCell>
                    <TableCell sx={{ color: '#4CA1AF', fontWeight: 700, fontSize: '0.82rem' }}>ORGANIZATION</TableCell>
                    <TableCell sx={{ color: '#4CA1AF', fontWeight: 700, fontSize: '0.82rem' }}>AGREEMENT STATUS & SIGNATURE</TableCell>
                    <TableCell sx={{ color: '#4CA1AF', fontWeight: 700, fontSize: '0.82rem' }}>DATE / CERTIFICATE</TableCell>
                    <TableCell align="right" sx={{ color: '#4CA1AF', fontWeight: 700, fontSize: '0.82rem' }}>ACTIONS</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredAdmins.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'rgba(255, 255, 255, 0.5)' }}>
                        {loadingAdmins ? 'Loading exact users from database...' : 'No users matching the selected filters.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAdmins.map((admin) => (
                      <TableRow
                        key={admin.id}
                        sx={{
                          '&:hover': { background: 'rgba(76, 161, 175, 0.08)' },
                          borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                          transition: 'background 0.2s',
                        }}
                      >
                        {/* Admin Name & Email */}
                        <TableCell sx={{ color: '#FFFFFF' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                background: admin.accepted
                                   ? 'linear-gradient(135deg, #10B981 0%, #047857 100%)'
                                   : 'linear-gradient(135deg, #F59E0B 0%, #B45309 100%)',
                                fontWeight: 700,
                                fontSize: '0.9rem',
                              }}
                            >
                              {(admin.name || 'U').charAt(0).toUpperCase()}
                            </Avatar>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#FFFFFF', lineHeight: 1.2 }}>
                                {admin.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.55)', display: 'block' }}>
                                {admin.email}
                              </Typography>
                              {admin.phone && (
                                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontSize: '0.68rem' }}>
                                  {admin.phone}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </TableCell>

                        {/* Role */}
                        <TableCell>
                          <Chip
                            label={getDisplayRoleName(admin.role)}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.72rem',
                              fontWeight: 600,
                              background: 'rgba(76, 161, 175, 0.15)',
                              color: '#6DD5FA',
                              border: '1px solid rgba(76, 161, 175, 0.3)',
                            }}
                          />
                        </TableCell>

                        {/* Organization */}
                        <TableCell sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                            <BusinessIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.4)' }} />
                            <span>{admin.organization}</span>
                          </Box>
                        </TableCell>

                        {/* Agreement Status & Signature Type */}
                        <TableCell>
                          {admin.accepted ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
                              <Chip
                                icon={<CheckCircleIcon sx={{ fontSize: '14px !important', color: '#FFFFFF !important' }} />}
                                label="Accepted & Active"
                                size="small"
                                sx={{
                                  background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                                  color: '#FFFFFF',
                                  fontWeight: 700,
                                  fontSize: '0.72rem',
                                }}
                              />
                              {/* Signature Method Badge */}
                              {admin.signatureType === 'paper_upload' ? (
                                <Chip
                                  icon={<PhotoCameraIcon sx={{ fontSize: '12px !important', color: '#38BDF8 !important' }} />}
                                  label="Signed Paper Photo"
                                  size="small"
                                  onClick={() => setSignaturePreviewModal({ open: true, admin })}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.68rem',
                                    background: 'rgba(56, 189, 248, 0.15)',
                                    color: '#38BDF8',
                                    border: '1px solid rgba(56, 189, 248, 0.3)',
                                    cursor: 'pointer',
                                    '&:hover': { background: 'rgba(56, 189, 248, 0.25)' },
                                  }}
                                />
                              ) : admin.signatureType === 'digital_draw' ? (
                                <Chip
                                  icon={<DrawIcon sx={{ fontSize: '12px !important', color: '#A78BFA !important' }} />}
                                  label="Digital Sign"
                                  size="small"
                                  onClick={() => setSignaturePreviewModal({ open: true, admin })}
                                  sx={{
                                    height: 20,
                                    fontSize: '0.68rem',
                                    background: 'rgba(167, 139, 250, 0.15)',
                                    color: '#C4B5FD',
                                    border: '1px solid rgba(167, 139, 250, 0.3)',
                                    cursor: 'pointer',
                                    '&:hover': { background: 'rgba(167, 139, 250, 0.25)' },
                                  }}
                                />
                              ) : (
                                <Chip
                                  icon={<EditNoteIcon sx={{ fontSize: '12px !important', color: '#FCD34D !important' }} />}
                                  label="Typed Legal Name"
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: '0.68rem',
                                    background: 'rgba(252, 211, 77, 0.12)',
                                    color: '#FCD34D',
                                  }}
                                />
                              )}
                            </Box>
                          ) : (
                            <Chip
                              icon={<WarningAmberIcon sx={{ fontSize: '14px !important', color: '#1A202C !important' }} />}
                              label="Not Accepted (Pending)"
                              size="small"
                              sx={{
                                background: '#FBBF24',
                                color: '#1A202C',
                                fontWeight: 700,
                                fontSize: '0.72rem',
                              }}
                            />
                          )}
                        </TableCell>

                        {/* Date / Certificate Code */}
                        <TableCell sx={{ color: 'rgba(255, 255, 255, 0.75)', fontSize: '0.8rem' }}>
                          {admin.accepted ? (
                            <Box>
                              <Typography variant="caption" sx={{ display: 'block', color: '#34D399', fontWeight: 600 }}>
                                {admin.acceptedAt ? new Date(admin.acceptedAt).toLocaleDateString() : 'Active'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', fontFamily: 'monospace' }}>
                                {admin.verificationCode || 'VERIFIED'}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="caption" sx={{ color: '#F87171', fontStyle: 'italic' }}>
                              Awaiting signature
                            </Typography>
                          )}
                        </TableCell>

                        {/* Actions */}
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                            {admin.accepted ? (
                              <>
                                <Tooltip title="View Certificate with Signature">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => setSelectedCertificate(admin)}
                                    sx={{
                                      fontSize: '0.75rem',
                                      py: 0.3,
                                      px: 1,
                                      color: '#34D399',
                                      borderColor: 'rgba(52, 211, 153, 0.4)',
                                      textTransform: 'none',
                                      '&:hover': {
                                        background: 'rgba(52, 211, 153, 0.15)',
                                        borderColor: '#34D399',
                                      },
                                    }}
                                  >
                                    Certificate
                                  </Button>
                                </Tooltip>
                                {admin.signatureImage && (
                                  <Tooltip title="View Signature Image / Paper Photo">
                                    <IconButton
                                      size="small"
                                      onClick={() => setSignaturePreviewModal({ open: true, admin })}
                                      sx={{ color: '#38BDF8', '&:hover': { background: 'rgba(56, 189, 248, 0.15)' } }}
                                    >
                                      <VisibilityIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Revoke Agreement">
                                  <Button
                                    size="small"
                                    onClick={() => handleToggleAdminStatus(admin, false)}
                                    sx={{
                                      fontSize: '0.72rem',
                                      py: 0.2,
                                      px: 0.8,
                                      color: 'rgba(255, 255, 255, 0.4)',
                                      textTransform: 'none',
                                      '&:hover': { color: '#EF4444' },
                                    }}
                                  >
                                    Revoke
                                  </Button>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <Tooltip title="Send reminder notification">
                                  <Button
                                    size="small"
                                    variant="contained"
                                    startIcon={<SendIcon sx={{ fontSize: '13px !important' }} />}
                                    onClick={() => handleSendReminder(admin)}
                                    sx={{
                                      fontSize: '0.75rem',
                                      py: 0.3,
                                      px: 1.2,
                                      background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                                      color: '#1A202C',
                                      fontWeight: 700,
                                      textTransform: 'none',
                                      '&:hover': {
                                        background: 'linear-gradient(135deg, #D97706 0%, #B45309 100%)',
                                      },
                                    }}
                                  >
                                    Remind
                                  </Button>
                                </Tooltip>
                                <Tooltip title="Upload signed white paper photo or record signature for this user">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<PhotoCameraIcon sx={{ fontSize: '13px !important' }} />}
                                    onClick={() => handleOpenAdminUploadModal(admin)}
                                    sx={{
                                      fontSize: '0.75rem',
                                      py: 0.3,
                                      px: 1,
                                      color: '#38BDF8',
                                      borderColor: 'rgba(56, 189, 248, 0.4)',
                                      textTransform: 'none',
                                      '&:hover': {
                                        background: 'rgba(56, 189, 248, 0.15)',
                                        borderColor: '#38BDF8',
                                      },
                                    }}
                                  >
                                    Upload Paper
                                  </Button>
                                </Tooltip>
                                <Tooltip title="Directly mark accepted">
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleToggleAdminStatus(admin, true)}
                                    sx={{
                                      fontSize: '0.75rem',
                                      py: 0.3,
                                      px: 0.8,
                                      color: '#4CA1AF',
                                      borderColor: 'rgba(76, 161, 175, 0.4)',
                                      textTransform: 'none',
                                      '&:hover': {
                                        background: 'rgba(76, 161, 175, 0.15)',
                                        borderColor: '#4CA1AF',
                                      },
                                    }}
                                  >
                                    Mark
                                  </Button>
                                </Tooltip>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* TAB 1: AGREEMENT POLICY DOCUMENT & COVENANT */}
        {activeTab === 1 && (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 4 },
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
              border: '1px solid rgba(76, 161, 175, 0.25)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                  GPN Administrator Covenant & Operating Standards
                </Typography>
                <Typography variant="caption" sx={{ color: '#4CA1AF' }}>
                  Version 2.4.0 • Applicable to all Organization Administrators, Staff, Leaders, and Intercessors
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
                sx={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  textTransform: 'none',
                }}
              >
                Print Document
              </Button>
            </Box>

            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }} />

            {/* Agreement Sections Accordion */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Accordion
                defaultExpanded
                sx={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px !important',
                  color: '#FFFFFF',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#4CA1AF' }} />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <ShieldIcon sx={{ color: '#4CA1AF', fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                      1. Sacred Stewardship & Ministry Purpose
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.88rem', lineHeight: 1.7 }}>
                  Administrators agree that the GPN platform exists to glorify God, unite believers worldwide in prayer, and foster genuine Christian fellowship. All administrative powers must be exercised in accordance with biblical stewardship, humility, love, and integrity.
                </AccordionDetails>
              </Accordion>

              <Accordion
                defaultExpanded
                sx={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px !important',
                  color: '#FFFFFF',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#4CA1AF' }} />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <LockIcon sx={{ color: '#4CA1AF', fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                      2. Strict Prayer Confidentiality & Privacy Safeguards
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.88rem', lineHeight: 1.7 }}>
                  Prayer requests submitted privately by members and intercessors must be treated with sacred confidence. Administrators are strictly forbidden from disclosing, exporting, or discussing confidential prayer burdens outside authorized ministry prayer circles.
                </AccordionDetails>
              </Accordion>

              <Accordion
                sx={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px !important',
                  color: '#FFFFFF',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#4CA1AF' }} />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <GavelIcon sx={{ color: '#4CA1AF', fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                      3. Content Moderation & Community Guidelines
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.88rem', lineHeight: 1.7 }}>
                  Administrators are responsible for actively monitoring public prayer requests, devotions, testimonies, and events. Any content containing harassment, false doctrine, commercial solicitations, hate speech, or inappropriate imagery must be removed promptly.
                </AccordionDetails>
              </Accordion>

              <Accordion
                sx={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px !important',
                  color: '#FFFFFF',
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#4CA1AF' }} />}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <VerifiedUserIcon sx={{ color: '#4CA1AF', fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                      4. Ride Connect & Benevolence Safety
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.88rem', lineHeight: 1.7 }}>
                  When coordinating church rides and material generosity through Share & Accept, administrators must prioritize personal safety, verify driver registrations, and prevent fraudulent item requests.
                </AccordionDetails>
              </Accordion>
            </Box>
          </Paper>
        )}

        {/* Certificate Modal Dialog with Embedded Signature Drawing or Uploaded Paper Photo */}
        <Dialog
          open={Boolean(selectedCertificate)}
          onClose={() => setSelectedCertificate(null)}
          PaperProps={{
            sx: {
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              color: '#FFFFFF',
              borderRadius: 3,
              border: '2px solid rgba(16, 185, 129, 0.5)',
              maxWidth: 560,
              width: '100%',
              p: 1,
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <VerifiedUserIcon sx={{ color: '#10B981', fontSize: 26 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Admin Agreement Certificate
              </Typography>
            </Box>
            <IconButton onClick={() => setSelectedCertificate(null)} sx={{ color: 'rgba(255,255,255,0.6)' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
            {selectedCertificate && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    background: 'rgba(16, 185, 129, 0.08)',
                    border: '1px dashed rgba(16, 185, 129, 0.4)',
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="caption" sx={{ color: '#34D399', fontWeight: 700, letterSpacing: '1px' }}>
                    OFFICIAL GPN VERIFICATION CERTIFICATE
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#FFFFFF', mt: 1 }}>
                    {selectedCertificate.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4CA1AF', fontWeight: 600 }}>
                    {selectedCertificate.roleName || getDisplayRoleName(selectedCertificate.role)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mt: 0.5 }}>
                    {selectedCertificate.organization}
                  </Typography>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Certificate Code</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#38BDF8', fontWeight: 600 }}>
                      {selectedCertificate.verificationCode || 'GPN-AGR-VERIFIED'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Accepted Date</Typography>
                    <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                      {selectedCertificate.acceptedAt ? new Date(selectedCertificate.acceptedAt).toLocaleDateString() : 'Active'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Signature Method</Typography>
                    <Typography variant="body2" sx={{ color: '#A78BFA', fontWeight: 600 }}>
                      {selectedCertificate.signatureType === 'paper_upload' ? '📸 Signed White Paper Photo' : '✍️ Digital Signature'}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>Compliance Status</Typography>
                    <Typography variant="body2" sx={{ color: '#34D399', fontWeight: 700 }}>
                      Verified & Active
                    </Typography>
                  </Box>
                </Box>

                {/* Embedded Signature Image (Canvas Drawing OR Uploaded Paper Photo) */}
                <Box sx={{ p: 2, background: 'rgba(255, 255, 255, 0.04)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 1, fontWeight: 600 }}>
                    VERIFIED SIGNATURE ON FILE:
                  </Typography>
                  {selectedCertificate.signatureImage ? (
                    <Box
                      sx={{
                        p: 1.5,
                        background: '#FFFFFF',
                        borderRadius: 2,
                        textAlign: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: 90,
                      }}
                    >
                      <img
                        src={selectedCertificate.signatureImage}
                        alt="Administrator Signature"
                        style={{
                          maxHeight: 120,
                          maxWidth: '100%',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                      />
                    </Box>
                  ) : (
                    <Typography
                      variant="h5"
                      sx={{
                        fontFamily: 'serif',
                        fontStyle: 'italic',
                        color: '#4CA1AF',
                        borderBottom: '1px solid rgba(76, 161, 175, 0.5)',
                        display: 'inline-block',
                        px: 3,
                        py: 0.5,
                      }}
                    >
                      {selectedCertificate.signature || selectedCertificate.name}
                    </Typography>
                  )}
                  {selectedCertificate.paperFileName && (
                    <Typography variant="caption" sx={{ color: '#38BDF8', display: 'block', mt: 1, textAlign: 'center' }}>
                      📄 Source File: {selectedCertificate.paperFileName}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
              sx={{ color: '#4CA1AF', textTransform: 'none' }}
            >
              Print Certificate
            </Button>
            <Button
              variant="contained"
              onClick={() => setSelectedCertificate(null)}
              sx={{
                background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                textTransform: 'none',
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Signature Lightbox Modal (For viewing white paper photo or canvas drawing in high-res) */}
        <Dialog
          open={signaturePreviewModal.open}
          onClose={() => setSignaturePreviewModal({ open: false, admin: null })}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              background: '#0F172A',
              color: '#FFFFFF',
              borderRadius: 3,
              border: '1px solid rgba(76, 161, 175, 0.4)',
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ZoomInIcon sx={{ color: '#38BDF8', fontSize: 26 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Signature Verification: {signaturePreviewModal.admin?.name}
              </Typography>
            </Box>
            <IconButton onClick={() => setSignaturePreviewModal({ open: false, admin: null })} sx={{ color: 'rgba(255,255,255,0.6)' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)', textAlign: 'center', py: 4 }}>
            {signaturePreviewModal.admin?.signatureImage ? (
              <Box>
                <Typography variant="subtitle2" sx={{ color: '#94A3B8', mb: 2 }}>
                  {signaturePreviewModal.admin.signatureType === 'paper_upload'
                    ? '📸 Photo of Signature on Physical White Paper'
                    : '✍️ Digitally Drawn Signature'}
                </Typography>
                <Paper
                  elevation={4}
                  sx={{
                    p: 2,
                    background: '#FFFFFF',
                    borderRadius: 2,
                    display: 'inline-block',
                    maxWidth: '100%',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                  }}
                >
                  <img
                    src={signaturePreviewModal.admin.signatureImage}
                    alt="Signature Document"
                    style={{
                      maxHeight: 380,
                      maxWidth: '100%',
                      objectFit: 'contain',
                      borderRadius: 4,
                    }}
                  />
                </Paper>
                {signaturePreviewModal.admin.paperFileName && (
                  <Typography variant="body2" sx={{ color: '#38BDF8', mt: 2 }}>
                    File Name: <strong>{signaturePreviewModal.admin.paperFileName}</strong>
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ py: 4 }}>
                <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                  Typed Signature on File:
                </Typography>
                <Typography variant="h4" sx={{ fontFamily: 'serif', fontStyle: 'italic', color: '#FCD34D', mt: 2 }}>
                  "{signaturePreviewModal.admin?.signature || signaturePreviewModal.admin?.name}"
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setSignaturePreviewModal({ open: false, admin: null })} sx={{ color: '#4CA1AF' }}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Super Admin Modal: Upload Signed Paper for a User */}
        <Dialog
          open={adminUploadModal.open}
          onClose={() => setAdminUploadModal({ open: false, admin: null, photoUrl: null, fileName: '', notes: '' })}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              color: '#FFFFFF',
              borderRadius: 3,
              border: '1px solid rgba(56, 189, 248, 0.4)',
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <PhotoCameraIcon sx={{ color: '#38BDF8', fontSize: 26 }} />
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Upload Signed White Paper Photo
              </Typography>
            </Box>
            <IconButton onClick={() => setAdminUploadModal({ open: false, admin: null, photoUrl: null, fileName: '', notes: '' })} sx={{ color: 'rgba(255,255,255,0.6)' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 2 }}>
              Upload the photo of the signed white paper received from <strong>{adminUploadModal.admin?.name}</strong> ({adminUploadModal.admin?.email}) to verify and accept their agreement.
            </Typography>

            <input
              ref={adminModalFileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={(e) => handleAdminModalPhotoSelect(e.target.files?.[0])}
            />

            {adminUploadModal.photoUrl ? (
              <Box sx={{ textAlign: 'center', p: 2, background: 'rgba(255,255,255,0.03)', borderRadius: 2, border: '1px solid rgba(56,189,248,0.3)' }}>
                <Paper sx={{ p: 1.5, background: '#FFFFFF', borderRadius: 2, display: 'inline-block', mb: 1.5 }}>
                  <img
                    src={adminUploadModal.photoUrl}
                    alt="Signed Paper Preview"
                    style={{ maxHeight: 200, maxWidth: '100%', objectFit: 'contain' }}
                  />
                </Paper>
                <Typography variant="body2" sx={{ color: '#38BDF8', fontWeight: 600 }}>
                  {adminUploadModal.fileName}
                </Typography>
                <Button
                  size="small"
                  onClick={() => adminModalFileInputRef.current?.click()}
                  sx={{ mt: 1, color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}
                >
                  Change Photo
                </Button>
              </Box>
            ) : (
              <Box
                onClick={() => adminModalFileInputRef.current?.click()}
                sx={{
                  border: '2px dashed rgba(56, 189, 248, 0.4)',
                  borderRadius: 2.5,
                  p: 4,
                  textAlign: 'center',
                  background: 'rgba(56, 189, 248, 0.05)',
                  cursor: 'pointer',
                  '&:hover': { background: 'rgba(56, 189, 248, 0.1)', borderColor: '#38BDF8' },
                }}
              >
                <CloudUploadIcon sx={{ fontSize: 44, color: '#38BDF8', mb: 1 }} />
                <Typography variant="subtitle1" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                  Click to Browse Photo of Signed White Paper
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  Accepts JPG, PNG, WEBP images of handwritten signature
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setAdminUploadModal({ open: false, admin: null, photoUrl: null, fileName: '', notes: '' })}
              sx={{ color: 'rgba(255,255,255,0.7)' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!adminUploadModal.photoUrl}
              onClick={handleSaveAdminUploadedPaper}
              sx={{
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: '#FFFFFF',
                fontWeight: 700,
                textTransform: 'none',
              }}
            >
              Verify & Mark Accepted
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reminder Modal Dialog */}
        <Dialog
          open={reminderDialog.open}
          onClose={() => setReminderDialog({ open: false, admin: null })}
          PaperProps={{
            sx: {
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
              color: '#FFFFFF',
              borderRadius: 3,
              border: '1px solid rgba(245, 158, 11, 0.4)',
              maxWidth: 480,
            },
          }}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <WarningAmberIcon sx={{ color: '#FBBF24' }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Send Agreement Reminder
            </Typography>
          </DialogTitle>
          <DialogContent dividers sx={{ borderColor: 'rgba(255,255,255,0.1)' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mb: 2 }}>
              Send an administrative notification to <strong>{reminderDialog.admin?.name}</strong> at <strong>{reminderDialog.admin?.email}</strong> requesting them to review and complete the GPN Administrator Agreement.
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <Typography variant="caption" sx={{ color: '#4CA1AF', display: 'block', mb: 0.5, fontWeight: 700 }}>
                MESSAGE PREVIEW:
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)', lineHeight: 1.6, display: 'block' }}>
                "Dear {reminderDialog.admin?.name}, this is a reminder from GPN Leadership to please complete your administrator agreement at {window.location.origin}/admin/admin-agreement (you can sign digitally or upload a picture of your signature on white paper)."
              </Typography>
            </Paper>
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              startIcon={<ContentCopyIcon />}
              onClick={handleCopyReminderLink}
              sx={{ color: '#4CA1AF', textTransform: 'none' }}
            >
              Copy Text
            </Button>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={handleConfirmSendReminder}
              sx={{
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                color: '#1A202C',
                fontWeight: 700,
                textTransform: 'none',
              }}
            >
              Send Notification
            </Button>
          </DialogActions>
        </Dialog>

        {/* Global Toast */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })} sx={{ width: '100%' }}>
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // -------------------------------------------------------------
  // RENDER: NON-SUPER ADMIN AGREEMENT FORM & DIGITAL SIGNING VIEW
  // -------------------------------------------------------------
  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, maxWidth: 1000, mx: 'auto' }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/admin/dashboard')}
        sx={{
          color: '#4CA1AF',
          textTransform: 'none',
          mb: 2,
          '&:hover': { background: 'rgba(76, 161, 175, 0.1)' },
        }}
      >
        Back to Dashboard
      </Button>

      {/* Page Header */}
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <ShieldIcon sx={{ fontSize: 36, color: '#4CA1AF' }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              background: 'linear-gradient(90deg, #FFFFFF 0%, #4CA1AF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontSize: { xs: '1.75rem', sm: '2.25rem' },
            }}
          >
            GPN Administrator Agreement
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.75)', maxWidth: 650, mx: 'auto' }}>
          Code of Conduct, Prayer Confidentiality, and Ministry Stewardship Guidelines for Glocal Prayer Network Administrators.
        </Typography>
      </Box>

      {/* Success Notification */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* If already completed and not in reaffirm mode */}
      {isCompleted && !showReaffirm && agreementRecord && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4 },
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(15, 23, 42, 0.9) 100%)',
            border: '2px solid rgba(16, 185, 129, 0.45)',
            boxShadow: '0 8px 32px rgba(16, 185, 129, 0.15)',
            mb: 4,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CheckCircleIcon sx={{ color: '#10B981', fontSize: 36 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF' }}>
                Agreement Completed & Active
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                You have fulfilled the GPN Administrator Agreement requirements.
              </Typography>
            </Box>
            <Chip
              label="Active & Verified"
              color="success"
              size="small"
              sx={{ ml: 'auto', fontWeight: 700, px: 1 }}
            />
          </Box>

          <Divider sx={{ my: 2, borderColor: 'rgba(16, 185, 129, 0.3)' }} />

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.55)' }}>
                Administrator
              </Typography>
              <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                {agreementRecord.adminName}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.55)' }}>
                Administrative Email
              </Typography>
              <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                {agreementRecord.adminEmail}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.55)' }}>
                Organization
              </Typography>
              <Typography variant="body1" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                {agreementRecord.organization}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.55)' }}>
                Verification Certificate Code
              </Typography>
              <Typography variant="body1" sx={{ color: '#4CA1AF', fontWeight: 700, fontFamily: 'monospace' }}>
                {agreementRecord.verificationCode}
              </Typography>
            </Grid>
          </Grid>

          {/* Display Verified Signature Image on Record */}
          {agreementRecord.signatureImage && (
            <Box sx={{ mb: 3, p: 2, background: 'rgba(255, 255, 255, 0.04)', borderRadius: 2 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', display: 'block', mb: 1 }}>
                YOUR VERIFIED SIGNATURE ON FILE:
              </Typography>
              <Paper sx={{ p: 1.5, background: '#FFFFFF', borderRadius: 2, display: 'inline-block' }}>
                <img
                  src={agreementRecord.signatureImage}
                  alt="Verified Signature"
                  style={{ maxHeight: 90, maxWidth: 300, objectFit: 'contain' }}
                />
              </Paper>
              {agreementRecord.paperFileName && (
                <Typography variant="caption" sx={{ color: '#38BDF8', display: 'block', mt: 0.5 }}>
                  Uploaded White Paper Photo: {agreementRecord.paperFileName}
                </Typography>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={() => window.print()}
              sx={{
                color: '#4CA1AF',
                borderColor: '#4CA1AF',
                textTransform: 'none',
                '&:hover': { background: 'rgba(76, 161, 175, 0.1)' },
              }}
            >
              Print Certificate
            </Button>
            <Button
              variant="text"
              onClick={() => setShowReaffirm(true)}
              sx={{
                color: 'rgba(255, 255, 255, 0.65)',
                textTransform: 'none',
                '&:hover': { color: '#FFFFFF' },
              }}
            >
              Update / Re-sign Agreement
            </Button>
          </Box>
        </Paper>
      )}

      {/* Main Agreement Terms and Signing Form */}
      {(!isCompleted || showReaffirm) && (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: 3,
            background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.95) 100%)',
            border: '1px solid rgba(76, 161, 175, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          }}
        >
          {/* Status Alert if Pending */}
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{
              mb: 3,
              borderRadius: 2,
              background: 'rgba(245, 158, 11, 0.15)',
              border: '1px solid rgba(245, 158, 11, 0.3)',
              color: '#FBBF24',
              '& .MuiAlert-icon': { color: '#FBBF24' },
            }}
          >
            <strong>Action Required:</strong> Please review the clauses below and provide your signature. You can either <strong>draw your digital signature</strong> or <strong>sign on a plain white paper, take a picture, and upload it</strong>.
          </Alert>

          {/* Agreement Accordion Sections */}
          <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Accordion
              defaultExpanded
              sx={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px !important',
                color: '#FFFFFF',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#4CA1AF' }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <ShieldIcon sx={{ color: '#4CA1AF', fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 600 }}>
                    1. Sacred Purpose & Christian Stewardship
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.88rem', lineHeight: 1.7 }}>
                The Glocal Prayer Network (GPN) is dedicated to fostering global prayer, fellowship, and unity across the Body of Christ. As an administrator, you agree to lead with Christian humility, protect the church and its believers, and use all administrative features solely for the advancement of prayer, spiritual growth, and kingdom fellowship.
              </AccordionDetails>
            </Accordion>

            <Accordion
              defaultExpanded
              sx={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px !important',
                color: '#FFFFFF',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#4CA1AF' }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <LockIcon sx={{ color: '#4CA1AF', fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 600 }}>
                    2. Intercessor & Prayer Confidentiality
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.88rem', lineHeight: 1.7 }}>
                Believers often share deeply personal burdens, health matters, and family prayer requests through GPN. Administrators must treat all private prayer requests and member records with sacred confidentiality. You agree not to disclose, share, distribute, or discuss private prayer contents outside designated intercessory prayer chains.
              </AccordionDetails>
            </Accordion>

            <Accordion
              sx={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px !important',
                color: '#FFFFFF',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#4CA1AF' }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <GavelIcon sx={{ color: '#4CA1AF', fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 600 }}>
                    3. Content Moderation & Safe Community Standards
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.88rem', lineHeight: 1.7 }}>
                Administrators are responsible for safeguarding their organization’s community feed. Public prayers, testimonies, devotions, and song submissions must be reviewed to ensure they are free of profanity, hate speech, political propaganda, commercial solicitation, or unauthorized personal data.
              </AccordionDetails>
            </Accordion>

            <Accordion
              sx={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px !important',
                color: '#FFFFFF',
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#4CA1AF' }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <VerifiedUserIcon sx={{ color: '#4CA1AF', fontSize: 20 }} />
                  <Typography sx={{ fontWeight: 600 }}>
                    4. Ride Connect & Benevolence (Share & Accept) Safety
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0, color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.88rem', lineHeight: 1.7 }}>
                When managing community transportation through Ride Connect or physical donation requests through Share & Accept, administrators must enforce basic safety verification, verify driver validity, and ensure all items donated are wholesome and safe for recipients.
              </AccordionDetails>
            </Accordion>
          </Box>

          <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.15)' }} />

          {/* Form Fields */}
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#FFFFFF', mb: 2 }}>
            Administrator Credentials
          </Typography>

          {formError && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
              {formError}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Administrator Full Name"
                  required
                  value={adminName}
                  onChange={(e) => setAdminName(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      background: 'rgba(255, 255, 255, 0.04)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover fieldset': { borderColor: '#4CA1AF' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Administrative Email Address"
                  type="email"
                  required
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      background: 'rgba(255, 255, 255, 0.04)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover fieldset': { borderColor: '#4CA1AF' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Church / Organization Name"
                  value={organization}
                  onChange={(e) => setOrganization(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: '#FFFFFF',
                      background: 'rgba(255, 255, 255, 0.04)',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                      '&:hover fieldset': { borderColor: '#4CA1AF' },
                    },
                    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreeTerms}
                      onChange={(e) => setAgreeTerms(e.target.checked)}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        '&.Mui-checked': { color: '#4CA1AF' },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      I have read, understood, and agree to uphold all provisions of the <strong>GPN Administrator Agreement</strong>, Terms & Conditions, and Privacy Policy.
                    </Typography>
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={agreeConfidentiality}
                      onChange={(e) => setAgreeConfidentiality(e.target.checked)}
                      sx={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        '&.Mui-checked': { color: '#4CA1AF' },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      I solemnly pledge to protect the confidentiality and sanctity of all prayer requests and personal data entrusted to this platform.
                    </Typography>
                  }
                />
              </Grid>
            </Grid>

            {/* DUAL SIGNATURE SECTION: DIGITAL SIGN OR UPLOAD WHITE PAPER PHOTO */}
            <Box
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 2.5,
                background: 'rgba(15, 23, 42, 0.6)',
                border: '1px solid rgba(76, 161, 175, 0.35)',
                mb: 3,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#FFFFFF', mb: 1 }}>
                Signature Verification Options
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', mb: 2, fontSize: '0.85rem' }}>
                Choose how you want to sign the agreement: Draw directly on the digital pad, or sign on white paper and upload a clear photo.
              </Typography>

              {/* Mode Tabs */}
              <Tabs
                value={signMode}
                onChange={(e, val) => setSignMode(val)}
                sx={{
                  mb: 2.5,
                  minHeight: 44,
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 2,
                  p: 0.5,
                  '& .MuiTabs-indicator': { display: 'none' },
                  '& .MuiTab-root': {
                    minHeight: 38,
                    borderRadius: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    color: 'rgba(255, 255, 255, 0.7)',
                    transition: 'all 0.2s',
                    '&.Mui-selected': {
                      background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                      color: '#FFFFFF',
                      boxShadow: '0 2px 8px rgba(76, 161, 175, 0.3)',
                    },
                  },
                }}
              >
                <Tab icon={<DrawIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="✍️ Draw Digital Sign" />
                <Tab icon={<PhotoCameraIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="📸 Upload Signed Paper Photo" />
              </Tabs>

              {/* OPTION 1: DRAW DIGITAL SIGNATURE */}
              {signMode === 0 && (
                <Box>
                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block', mb: 1 }}>
                    Use your mouse, trackpad, finger, or stylus to draw your legal signature on the pad below:
                  </Typography>
                  <Box
                    sx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      border: '2px solid rgba(76, 161, 175, 0.4)',
                      background: '#FFFFFF',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                    }}
                  >
                    <canvas
                      ref={canvasRef}
                      width={700}
                      height={190}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      style={{
                        width: '100%',
                        height: '190px',
                        display: 'block',
                        cursor: 'crosshair',
                        touchAction: 'none',
                      }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Typography variant="caption" sx={{ color: hasDrawnSignature ? '#34D399' : 'rgba(255, 255, 255, 0.5)' }}>
                      {hasDrawnSignature ? '✓ Signature recorded' : 'Awaiting signature drawing...'}
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={clearSignatureCanvas}
                      sx={{ color: '#EF4444', textTransform: 'none', fontSize: '0.78rem' }}
                    >
                      Clear & Redraw
                    </Button>
                  </Box>
                </Box>
              )}

              {/* OPTION 2: UPLOAD WHITE PAPER SIGNED PHOTO */}
              {signMode === 1 && (
                <Box>
                  <Box sx={{ p: 2, mb: 2, borderRadius: 2, background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.25)' }}>
                    <Typography variant="subtitle2" sx={{ color: '#38BDF8', fontWeight: 700, mb: 0.5 }}>
                      How to sign on paper:
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#E0F2FE', lineHeight: 1.6, display: 'block' }}>
                      1. Take a plain white paper.<br />
                      2. Write your legal signature clearly with blue or black ink.<br />
                      3. Take a well-lit photo using your phone camera or scan the paper.<br />
                      4. Upload the image file (JPG, PNG) below.
                    </Typography>
                  </Box>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={(e) => handlePaperPhotoSelect(e.target.files?.[0])}
                  />

                  {paperPhotoData.url ? (
                    <Box
                      sx={{
                        p: 2.5,
                        borderRadius: 2,
                        background: 'rgba(255, 255, 255, 0.04)',
                        border: '1px solid rgba(56, 189, 248, 0.4)',
                        textAlign: 'center',
                      }}
                    >
                      <Paper
                        elevation={3}
                        sx={{
                          p: 1.5,
                          background: '#FFFFFF',
                          borderRadius: 2,
                          display: 'inline-block',
                          maxWidth: '100%',
                          mb: 1.5,
                        }}
                      >
                        <img
                          src={paperPhotoData.url}
                          alt="Uploaded White Paper Signature"
                          style={{
                            maxHeight: 180,
                            maxWidth: '100%',
                            objectFit: 'contain',
                            display: 'block',
                          }}
                        />
                      </Paper>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                        <FilePresentIcon sx={{ color: '#38BDF8', fontSize: 20 }} />
                        <Typography variant="body2" sx={{ color: '#FFFFFF', fontWeight: 600 }}>
                          {paperPhotoData.name} ({paperPhotoData.size})
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => fileInputRef.current?.click()}
                          sx={{ color: '#38BDF8', borderColor: 'rgba(56, 189, 248, 0.5)', textTransform: 'none' }}
                        >
                          Change Photo
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setPaperPhotoData({ url: null, name: '', size: '', file: null })}
                          sx={{ textTransform: 'none' }}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      onDragOver={(e) => {
                        e.preventDefault();
                        setIsDragging(true);
                      }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setIsDragging(false);
                        handlePaperPhotoSelect(e.dataTransfer.files?.[0]);
                      }}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        border: '2px dashed rgba(56, 189, 248, 0.5)',
                        borderRadius: 2.5,
                        p: 4,
                        textAlign: 'center',
                        background: isDragging ? 'rgba(56, 189, 248, 0.2)' : 'rgba(56, 189, 248, 0.05)',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        '&:hover': {
                          background: 'rgba(56, 189, 248, 0.1)',
                          borderColor: '#38BDF8',
                        },
                      }}
                    >
                      <PhotoCameraIcon sx={{ fontSize: 48, color: '#38BDF8', mb: 1.5 }} />
                      <Typography variant="subtitle1" sx={{ color: '#FFFFFF', fontWeight: 700 }}>
                        Click to Take / Upload Picture of Signed White Paper
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.65)', display: 'block', mt: 0.5 }}>
                        Drag and drop your signed paper photo or browse files (JPG, PNG, WEBP)
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}


            </Box>

            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {showReaffirm && (
                <Button
                  variant="outlined"
                  onClick={() => setShowReaffirm(false)}
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    textTransform: 'none',
                  }}
                >
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                size="large"
                startIcon={<VerifiedUserIcon />}
                sx={{
                  background: 'linear-gradient(135deg, #4CA1AF 0%, #2C3E50 100%)',
                  color: '#FFFFFF',
                  px: 4,
                  py: 1.2,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  boxShadow: '0 4px 15px rgba(76, 161, 175, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5AB3C2 0%, #34495E 100%)',
                  },
                }}
              >
                Sign & Accept Agreement
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default AdminAgreement;
